/* eslint-disable react/no-danger, jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import classnames from 'classnames';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { ChevronDownIcon, ChevronUpIcon } from '@fluentui/react-icons';
import { useSelector } from 'react-redux';
import { betreuerByNrSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { createKundeUrl, createRecordUrl, formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { AuftragStatusLabel } from 'utils/labels';
import { FilterValues, LineData } from './types';
import { FilterInfo } from './FilterInfo';
import { useRahmenauftraegeSummary } from './hooks/useRahmenauftraegeSummary';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    header: {
        marginTop: 35,
    },
    printHeader: {},
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        cursor: 'pointer',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    auNr: {},
    datum: {},
    text: {},
    ekNetto: {
        backgroundColor: even,
    },
    vkNetto: {
        backgroundColor: odd,
    },
    bMarge: {
        backgroundColor: even,
    },
    anzahlAbrufe: {
        backgroundColor: odd,
    },
    letzterAbruf: {
        backgroundColor: odd,
    },
    datBis: {
        backgroundColor: even,
    },
    borderRight: {
        borderRight: '1px solid',
    },
    note: {
        fontSize: 12,
        color: palette.neutralTertiary,
    },
});

interface RahmenauftraegeProps extends FilterValues {
    printView?: boolean;
    toggleSort: (column: string) => void;
}

export const Rahmenauftraege: React.FC<RahmenauftraegeProps> = (filterValues) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);

    const betreuerByNr = useSelector(betreuerByNrSelector);

    const { datumVon, datumBis, printView = false, sortColumn, sortDirection, toggleSort } = filterValues;

    const rahmenauftraegeSummary = useRahmenauftraegeSummary(filterValues);

    const sortedLineDataList = React.useMemo<LineData[]>(() => {
        if (sortColumn === 'status') {
            return rahmenauftraegeSummary.lineDataList.sort((a, b) =>
                sortDirection === 'asc'
                    ? (AuftragStatusLabel[a.status] || '')
                          .toLocaleLowerCase()
                          .localeCompare(AuftragStatusLabel[b.status] || '')
                    : (AuftragStatusLabel[b.status] || '')
                          .toLocaleLowerCase()
                          .localeCompare(AuftragStatusLabel[a.status] || ''),
            );
        }

        if (['auNr', 'text', 'datum', 'datBis', 'letzterAbruf'].includes(sortColumn)) {
            return rahmenauftraegeSummary.lineDataList.sort((a, b) =>
                sortDirection === 'asc'
                    ? ((a[sortColumn as keyof LineData] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((b[sortColumn as keyof LineData] as string) || '')
                    : ((b[sortColumn as keyof LineData] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((a[sortColumn as keyof LineData] as string) || ''),
            );
        }

        return rahmenauftraegeSummary.lineDataList.sort((a, b) =>
            sortDirection === 'asc'
                ? ((a[sortColumn as keyof LineData] as number) || 0) -
                  ((b[sortColumn as keyof LineData] as number) || 0)
                : ((b[sortColumn as keyof LineData] as number) || 0) -
                  ((a[sortColumn as keyof LineData] as number) || 0),
        );
    }, [rahmenauftraegeSummary.lineDataList, sortColumn, sortDirection]);

    const renderSortIcon = (column: string) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') {
                return <ChevronUpIcon className={formStyles.reportSortIcon} />;
            }
            return <ChevronDownIcon className={formStyles.reportSortIcon} />;
        }
        return null;
    };

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                    )}
                    onClick={() => toggleSort('auNr')}
                >
                    AuftragNr.
                    {renderSortIcon('auNr')}
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                    onClick={() => toggleSort('datum')}
                >
                    {renderSortIcon('datum')}
                    Datum
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                    onClick={() => toggleSort('text')}
                >
                    Kunde
                    {renderSortIcon('text')}
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        styles.borderRight,
                        printView ? formStyles.reportAuftragStatusPrintCell : formStyles.reportAuftragStatusCell,
                    )}
                    onClick={() => toggleSort('status')}
                >
                    Status
                    {renderSortIcon('status')}
                </td>
                <td className={classnames(moneyClassName, styles.vkNetto)} onClick={() => toggleSort('vkNettoRahmen')}>
                    {renderSortIcon('vkNettoRahmen')}
                    VK Netto
                    <br />
                    Rahmen
                </td>
                <td className={classnames(moneyClassName, styles.vkNetto)} onClick={() => toggleSort('vkNettoOffenP')}>
                    {renderSortIcon('vkNettoOffenP')}%
                </td>
                <td
                    className={classnames(moneyClassName, styles.vkNetto, styles.borderRight)}
                    onClick={() => toggleSort('vkNettoOffen')}
                >
                    {renderSortIcon('vkNettoOffen')}
                    VK Netto
                    <br />
                    Offen
                </td>
                <td className={classnames(moneyClassName, styles.bMarge)} onClick={() => toggleSort('bMargeRahmen')}>
                    {renderSortIcon('bMargeRahmen')}
                    B-Marge
                    <br />
                    Rahmen
                </td>
                <td className={classnames(moneyClassName, styles.ekNetto)} onClick={() => toggleSort('bMargeOffenP')}>
                    {renderSortIcon('bMargeOffenP')}%
                </td>
                <td
                    className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}
                    onClick={() => toggleSort('bMargeOffen')}
                >
                    {renderSortIcon('bMargeOffen')}
                    B-Marge
                    <br />
                    Offen
                </td>
                <td
                    className={classnames(moneyClassName, styles.anzahlAbrufe)}
                    onClick={() => toggleSort('anzahlAbrufe')}
                >
                    {renderSortIcon('anzahlAbrufe')}
                    Anzahl Abrufe
                </td>
                <td
                    className={classnames(
                        moneyClassName,
                        styles.letzterAbruf,
                        formStyles.dataValueRight,
                        styles.borderRight,
                    )}
                    onClick={() => toggleSort('letzterAbruf')}
                >
                    {renderSortIcon('letzterAbruf')}
                    Letzter Abruf
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                    onClick={() => toggleSort('datBis')}
                >
                    {renderSortIcon('datBis')}
                    Gültig Bis
                </td>
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentSmallPrintCell : formStyles.reportPercentSmallCell,
    );

    const renderLine = (line: LineData, key?: string, text?: string) => {
        return (
            <tr key={key || line.auNr}>
                <td className={styles.tdBody}>
                    {!printView ? (
                        <OpenLink link={createRecordUrl('auftraege', line.kNr, line.auNr)} label={line.auNr} />
                    ) : (
                        <div>{line.auNr}</div>
                    )}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {formatSmDate(line.datum, true)}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    {!printView && line.kNr ? (
                        <OpenLink link={createKundeUrl(line.kNr)} label={text || line.text} />
                    ) : (
                        <div>{text || line.text}</div>
                    )}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        styles.borderRight,
                        printView ? formStyles.reportAuftragStatusPrintCell : formStyles.reportAuftragStatusCell,
                    )}
                >
                    {AuftragStatusLabel[line.status] || line.status}
                </td>
                {/*
                <td className={classnames(moneyClassName, styles.ekNetto)}>{formatMoney(line.ekNettoRahmen)}</td>
                <td className={classnames(percentClassName, styles.ekNetto)}>{formatPercent(line.ekNettoP)}</td>
                <td className={classnames(moneyClassName, styles.ekNetto, styles.borderRight)}>
                    {formatMoney(line.ekNettoAbruf)}
                </td>
                */}
                <td className={classnames(moneyClassName, styles.vkNetto)}>{formatMoney(line.vkNettoRahmen)}</td>
                <td className={classnames(percentClassName, styles.vkNetto)}>{formatPercent(line.vkNettoOffenP)}</td>
                <td className={classnames(moneyClassName, styles.vkNetto, styles.borderRight)}>
                    <strong>{formatMoney(line.vkNettoOffen)}</strong>
                </td>
                <td className={classnames(moneyClassName, styles.bMarge)}>{formatMoney(line.bMargeRahmen)}</td>
                <td className={classnames(percentClassName, styles.bMarge)}>{formatPercent(line.bMargeOffenP)}</td>
                <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>
                    {formatMoney(line.bMargeOffen)}
                </td>
                <td className={classnames(moneyClassName, styles.anzahlAbrufe)}>{line.anzahlAbrufe}</td>
                <td
                    className={classnames(
                        styles.tdBody,
                        styles.letzterAbruf,
                        formStyles.dataValueRight,
                        styles.borderRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {formatSmDate(line.letzterAbruf, true)}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        styles.datBis,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {formatSmDate(line.datBis, true)}
                </td>
            </tr>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {rahmenauftraegeSummary.loading && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>
                    <thead>{renderHeaderRow()}</thead>
                </table>
            )}
            <h1 className={printView ? styles.printHeader : styles.header}>
                Rahmenaufträge
                {' (' + moment(datumVon).format('DD.MM.YYYY') + ' bis ' + moment(datumBis).format('DD.MM.YYYY') + ')'}
            </h1>
            {printView && <FilterInfo filterValues={filterValues} />}
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    {sortedLineDataList.map((line) => renderLine(line))}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {renderLine(
                        rahmenauftraegeSummary.total,
                        'total',
                        'Gesamt (' + rahmenauftraegeSummary.total.count + ')',
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {Object.keys(rahmenauftraegeSummary.betreuerTotal).map((betreuer) =>
                        renderLine(
                            rahmenauftraegeSummary.betreuerTotal[betreuer],
                            betreuer,
                            (betreuerByNr[betreuer].name || betreuer) +
                                ' (' +
                                rahmenauftraegeSummary.betreuerTotal[betreuer].count +
                                ')',
                        ),
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {Object.keys(rahmenauftraegeSummary.telBetreuerTotal).map((betreuer) =>
                        renderLine(
                            rahmenauftraegeSummary.telBetreuerTotal[betreuer],
                            betreuer,
                            (betreuerByNr[betreuer].name || betreuer) +
                                ' (' +
                                rahmenauftraegeSummary.telBetreuerTotal[betreuer].count +
                                ')',
                        ),
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
